import { Component, OnInit, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { HighchartsChartModule } from 'highcharts-angular';
import Highcharts from 'highcharts';
import { MetricsCardComponent } from './metrics-card/metrics-card.component';
import { FabricDataService } from '../fabric-data.service';
import { forkJoin, of, catchError, Observable } from 'rxjs';
import { UtilService } from '../services/util.service';
import { TableComponent } from '../shared/components/table/table.component';
import { DataTableComponent } from '../shared/components/data-table/data-table.component';
import { MatIconModule } from '@angular/material/icon';
import { exportTableAsCSV } from '../shared/utils/download.utils';
import { TableColumn, TableConfig, ColumnType } from '../models/table.models';
import { TableData } from '../models/api.models';
import funnelModule from 'highcharts/modules/funnel';
import exportingModule from 'highcharts/modules/exporting';
import HC_DataModule from "highcharts/modules/export-data";
import mapModule from 'highcharts/modules/map';
import { TrendDirection } from '../models/types';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field'; // Import MatError
import { MatOption, MatSelect } from '@angular/material/select';
import mapCollection from '@highcharts/map-collection/countries/gb/gb-all.topo.json';
import { FabricApiResponse, Metric } from '../models/api.models';
import { MatDatepickerModule } from '@angular/material/datepicker'; // Import MatDatepickerModule
import { MatNativeDateModule } from '@angular/material/core'; // Import MatNativeDateModule
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms'; // Import ReactiveFormsModule and form classes
import { MatInputModule } from '@angular/material/input'; // Import MatInputModule
import { MatButtonModule } from '@angular/material/button'; // Import MatButtonModule

// Do not remove this line, it is required for funnel chart to work
// eslint-disable-next-line no-unused-vars
const funnel = funnelModule;
const exporting = exportingModule;
const HC_Data = HC_DataModule;
const map = mapModule;

// Define dynamic date range boundaries for the picker
const MIN_START_DATE = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 1); // Set to yesterday
  d.setMonth(d.getMonth() - 1); // Set to last month relative to yesterday
  // d.setFullYear(d.getFullYear() - 1); // Set to last year relative to yesterday
  return d;
})();

const MAX_END_DATE = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 1); // Set to yesterday
  return d;
})();

// Define interface for metrics results
interface MetricResult {
  total: number;
  trend: TrendDirection;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    HighchartsChartModule,
    MetricsCardComponent,
    TableComponent,
    DataTableComponent, 
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    DatePipe,
    MatDatepickerModule, // Add MatDatepickerModule
    MatNativeDateModule, // Add MatNativeDateModule
    ReactiveFormsModule, // Add ReactiveFormsModule
    MatInputModule,      // Add MatInputModule
    MatButtonModule,     // Add MatButtonModule
    MatError,            // Add MatError
    MatIconModule        // Add MatIconModule
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  editView = false;
  
  Highcharts: typeof Highcharts = Highcharts;
  chartConstructor = 'chart';
  updateFlag = false;
  
  // Separate loading flags for different sections
  isChartsLoading = true;
  isSwitchAgeLoading = true;
  isMapLoading = true;
  isStatusSummaryLoading = true;
  isPendingIdsLoading = true;
  
  // Metrics with trend information
  metrics = {
    totalSwitches: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    totalMPANs: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    totalMPRNs: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    totalSmartMeterSwitches: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    smartMeterMPANs: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    smartMeterMPRNs: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },

    retentionSwitches: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    acquisitionSwitches: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    dualFuelSwitches: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
    elecOnlySwitches: { value: undefined as number | undefined, trend: 'neutral' as TrendDirection },
  };

  // Date range for API calls
  fromDate = '';
  toDate = '';

  switchAgeFromDate = '2023-12-06'
  
  // Date range picker properties
  minDate: Date = MIN_START_DATE;
  maxDate: Date = MAX_END_DATE;
  range = new FormGroup({
    start: new FormControl<Date | null>(null, Validators.required),
    end: new FormControl<Date | null>(null, Validators.required)
  });

  // Initialize empty chart configurations
  newEnergySupplierChart: Highcharts.Options = {};
  regionWiseChart: Highcharts.Options = {};
  tariffTypeChart: Highcharts.Options = {};
  switchTypeChart: Highcharts.Options = {};
  dayWiseChart: Highcharts.Options = {};
  switchStatusChart: Highcharts.Options = {};
  // switchCountChart: Highcharts.Options = {};
  switchAgeData: any = null;
  regionWiseMapChart: Highcharts.Options = {};

  displayData = signal<Array<any>>([]);
  tableColumns = signal<TableColumn[]>([]);
  tableConfig: TableConfig = {
    enablePagination: true,
    enableSort: true,
    enableSearch: true,
    enableFilter: true,
    enableCSVDownload: true,
    enableExcelDownload: true,
    showSerialNumber: true,
    stickyHeader: false,
    pageSize: 10,
    pageSizeOptions: [5, 10, 25, 50]
  };

  // Switch Status Summary table data
  switchStatusSummaryTableData = signal<TableData | null>(null);
  switchStatusSummaryConfig: Partial<TableConfig> = {
    enablePagination: false,
    enableSort: false,
    enableSearch: false,
    enableFilter: false,
    enableCSVDownload: true,
    enableExcelDownload: false,
    showSerialNumber: false,
    stickyHeader: false
  };

  // Switch Pending IDs data - for download only (no table display)
  pendingIdsDisplayData = signal<Array<any>>([]);
  pendingIdsTableColumns = signal<TableColumn[]>([]);

  // Updated to remove the 'All' option
  supplierOptions: { value: string, label: string }[] = [];
  selectedSupplier = '';
  isSuppliersLoading = true;
  noSuppliersFound = false;

  constructor(private fabricDataService: FabricDataService, private util: UtilService) {
    // Set default options for all charts
    Highcharts.setOptions({
      credits: { enabled: false }
    });
  }

  ngOnInit(): void {
    this.prepareDateRange();
    // Load suppliers first, then load other data after suppliers are loaded
    this.loadSuppliers();
  }

  prepareDateRange(): void {
    const dateRange = this.util.getWeeklyDateRange();
    this.fromDate = dateRange.fromDate;
    this.toDate = dateRange.toDate;
    // Set initial values for the date range picker form
    this.range.setValue({
      start: this.util.parseDateString(this.fromDate), // Convert string to Date
      end: this.util.parseDateString(this.toDate)     // Convert string to Date
    });
  }

  /**
   * Apply the selected date range and reload data.
   */
  applyDateChange(): void {
    if (this.range.valid && this.range.value.start && this.range.value.end) {
      this.editView = false; // Close the date picker
      this.fromDate = this.util.formatDate(this.range.value.start); // Format Date to string
      this.toDate = this.util.formatDate(this.range.value.end);     // Format Date to string

      // Reload all data based on the new date range
      this.reloadData();
    }
  }

  /**
   * Reloads all dashboard data based on current filters (supplier and date range).
   */
  reloadData(): void {
    // Reset loading states
    this.isChartsLoading = true;
    this.isSwitchAgeLoading = true;
    this.isMapLoading = true;
    this.isStatusSummaryLoading = true;
    this.isPendingIdsLoading = true;

    // Reset metrics values to indicate loading
    this.resetMetrics();

    // Reset chart configurations
    this.resetCharts();

    // Load data
    this.loadFabricData();
    this.loadChartData();
    this.loadSwitchAgeData();
    this.loadRegionWiseMap();
    this.loadSwitchStatusSummary();
    this.loadSwitchPendingIds();
  }

  /**
   * Resets metric values to undefined.
   */
  private resetMetrics(): void {
    this.metrics.totalSwitches.value = undefined;
    this.metrics.totalMPANs.value = undefined;
    this.metrics.totalMPRNs.value = undefined;
    this.metrics.totalSmartMeterSwitches.value = undefined;
    this.metrics.smartMeterMPANs.value = undefined;
    this.metrics.smartMeterMPRNs.value = undefined;
    this.metrics.retentionSwitches.value = undefined;
    this.metrics.acquisitionSwitches.value = undefined;
    this.metrics.dualFuelSwitches.value = undefined;
    this.metrics.elecOnlySwitches.value = undefined;
  }

  /**
   * Resets chart options to empty objects.
   */
  private resetCharts(): void {
    this.newEnergySupplierChart = {};
    this.regionWiseChart = {};
    this.tariffTypeChart = {};
    this.switchTypeChart = {};
    this.dayWiseChart = {};
    this.switchStatusChart = {};
    this.regionWiseMapChart = {};
    this.switchAgeData = null; // Reset table data source
    this.displayData.set([]); // Reset table display data
    this.updateFlag = false; // Reset update flag for charts
  }

  loadFabricData(): void {
    // Define metric endpoints to fetch
    const metricEndpoints = {
      mpans: 'elec_meter_types_switch_count',
      mprns: 'gas_meter_types_switch_count',
      total: 'total_switches'
    };
    
    // Create API request object for forkJoin with error handling
    const apiRequests: Record<string, Observable<FabricApiResponse<Metric[] | any>>> = {};
    
    // Build API requests with error handling
    Object.entries(metricEndpoints).forEach(([key, endpoint]) => {
      apiRequests[key] = this.fabricDataService.fetchFabricDataWithDate(endpoint, this.fromDate, this.toDate, this.selectedSupplier)
        .pipe(
          catchError(error => {
            console.error(`Error fetching ${endpoint} data:`, error);
            this.util.openSnackBar(`Error fetching ${endpoint.replace('_', ' ')} metrics`, 'Close', 'error');
            return of({ result: { data: [] } });
          })
        ) as Observable<FabricApiResponse<Metric[]>>;
    });
    
    // Fetch data from all APIs in parallel
    forkJoin(apiRequests).subscribe({
      next: (results) => {
        // Process MPAN data
        if (results?.['mpans']?.result?.data) {
          const mpanData = results['mpans'].result.data as Metric[];
          // Set total MPANs (from TotalCount type)
          const totalMpan = mpanData.find(item => item.type === 'TotalCount');
          if (totalMpan) {
            this.metrics.totalMPANs.value = totalMpan.count;
          }
          
          // Set Smart Meter MPANs
          const smartMpan = mpanData.find(item => item.type === 'SmartMeter');
          if (smartMpan) {
            this.metrics.smartMeterMPANs.value = smartMpan.count;
          }
        }
        
        // Process MPRN data
        if (results?.['mprns']?.result?.data) {
          const mprnData = results['mprns'].result.data as Metric[];
          // Set total MPRNs (from TotalCount type)
          const totalMprn = mprnData.find(item => item.type === 'TotalCount');
          if (totalMprn) {
            this.metrics.totalMPRNs.value = totalMprn.count;
          }
          
          // Set Smart Meter MPRNs
          const smartMprn = mprnData.find(item => item.type === 'SmartMeter');
          if (smartMprn) {
            this.metrics.smartMeterMPRNs.value = smartMprn.count;
          }
        }

        if (results?.['total'].result?.data) {

          const totalSwitchData = results['total'].result.data as any;
          const totalSwitches = totalSwitchData['totalSwitchIdCounts'] || 0;
          const retentionSwitches = totalSwitchData['retentionSwitchIdCounts'] || 0;
          const acquisitionSwitches = totalSwitchData['acquisitionSwitchIdCounts'] || 0;
          const dualFuelSwitches = totalSwitchData['duelFuelSwitchIdCounts'] || 0;
          const elecOnlySwitches = totalSwitchData['elecSwitchIdCounts'] || 0;

          this.metrics.totalSwitches.value = totalSwitches;
          this.metrics.retentionSwitches.value = retentionSwitches;
          this.metrics.acquisitionSwitches.value = acquisitionSwitches;
          this.metrics.dualFuelSwitches.value = dualFuelSwitches;
          this.metrics.elecOnlySwitches.value = elecOnlySwitches;
        }
        
        // Calculate total switches (sum of MPAN and MPRN totals)
        const mpanTotal = this.metrics.totalMPANs.value || 0;
        const mprnTotal = this.metrics.totalMPRNs.value || 0;
      //  this.metrics.totalSwitches.value = mpanTotal + mprnTotal;
        
        // Calculate total smart meter switches (sum of smart meter MPAN and MPRN)
        // const smartMpanTotal = this.metrics.smartMeterMPANs.value || 0;
        // const smartMprnTotal = this.metrics.smartMeterMPRNs.value || 0;
      //  this.metrics.totalSmartMeterSwitches.value = smartMpanTotal + smartMprnTotal;
      },
      error: (error) => {
        console.error('Unexpected error in loadFabricData:', error);
      }
    });
  }

  private convertStringFormatters(obj: any): void {
    if (!obj) return;

    // Helper function to convert string formatter to function
    const convertFormatter = (formatterStr: string): Function => {
      // Remove the "function() {" prefix and "}" suffix if present
      const functionBody = formatterStr
        .replace(/^function\s*\(\s*\)\s*\{/, '')
        .replace(/\}$/, '')
        .trim();
      // Create a new function from the string
      return new Function(functionBody);
    };

    // Handle formatter if the current object is a formatter string
    if (obj.formatter && typeof obj.formatter === 'string') {
      obj.formatter = convertFormatter(obj.formatter);
    }

    // Recursively process arrays
    if (Array.isArray(obj)) {
      obj.forEach(item => this.convertStringFormatters(item));
      return;
    }
    
    // Recursively process object properties
    if (typeof obj === 'object') {
      Object.entries(obj).forEach(([key, value]) => {
        if (value && typeof value === 'object') {
          this.convertStringFormatters(value);
        }
      });
    }
  }

  /**
   * Helper method to update chart options with data and common settings
   * @param chartOptions The chart options object to update
   * @param data The data to apply to the chart
   * @returns Boolean indicating if the chart was updated
   */
  private updateChartOptions(chartOptions: Highcharts.Options, data: any): boolean {
    if (!data?.result?.data) {
      chartOptions.series = []; // Ensure chart displays 'No data available'
      return false;
    }
    
    // Update chart options with data
    Object.assign(chartOptions, data.result.data);
    
    // Convert any string formatters to functions
    this.convertStringFormatters(chartOptions);
    
    // Apply common chart settings
    chartOptions.chart = {
      ...chartOptions.chart,
      backgroundColor: '#f7fafa'
    };
    
    return true;
  }

  loadChartData(): void {
    const defaultChartResult = { result: { data: null } };
    
    // Define chart endpoints to fetch
    const chartEndpoints = {
      newEnergySuppliers: 'new_energy_suppliers',
      regionWise: 'region_wise',
      tariffType: 'tariff_type',
      switchType: 'switch_type_v2',
      dayWise: 'day_wise',
      switchStatus: 'switch_status_v2',
      // switchCount: 'switch_count'
    };
    
    // Create API request object for forkJoin
    const apiRequests: Record<string, any> = {};
    
    // Build API requests with error handling
    Object.entries(chartEndpoints).forEach(([key, endpoint]) => {
      apiRequests[key] = this.fabricDataService.fetchFabricChartWithDate(endpoint, this.fromDate, this.toDate, this.selectedSupplier)
        .pipe(
          catchError(error => {
            console.error(`Error fetching ${endpoint} chart:`, error);
            this.util.openSnackBar(`Error fetching ${endpoint.replace('_', ' ')} chart`, 'Close', 'error');
            return of(defaultChartResult);
          })
        );
    });

    forkJoin(apiRequests).subscribe({
      next: (results) => {
        // Map of chart keys to their corresponding class properties
        const chartMap: Record<string, Highcharts.Options> = {
          newEnergySuppliers: this.newEnergySupplierChart,
          regionWise: this.regionWiseChart,
          tariffType: this.tariffTypeChart,
          switchType: this.switchTypeChart,
          dayWise: this.dayWiseChart,
          switchStatus: this.switchStatusChart,
          // switchCount: this.switchCountChart
        };
        
        // Update all charts with their respective data
        let hasUpdates = false;
        Object.entries(results).forEach(([key, data]) => {
          if (chartMap[key]) { // Check if the chart property exists
             if (this.updateChartOptions(chartMap[key], data)) {
               hasUpdates = true;
             }
          }
        });
        
        // Only set updateFlag if at least one chart was updated
        if (hasUpdates) {
          this.updateFlag = true;
        }
        
        // Set charts loading to false regardless of individual chart data availability
        this.isChartsLoading = false;
      },
      error: (error) => {
        // This should not trigger due to individual error handling above
        console.error('Unexpected error in loadChartData:', error);
        this.isChartsLoading = false;
      }
    });
  }
  
  loadSwitchAgeData(): void {
    // Use the main fromDate for switch age start date as well for consistency
    this.fabricDataService.fetchFabricChartWithDate('switch_age', this.switchAgeFromDate, this.toDate, this.selectedSupplier).pipe(
      catchError(error => {
        console.error('Error fetching switch age data:', error);
        this.util.openSnackBar('Error fetching switch age data', 'Close', 'error');
        return of({ result: { data: null } });
      })
    ).subscribe({
      next: (response) => {
        if (response.result?.data) {
          this.switchAgeData = response.result.data;
          
          // Transform chart data to table format
          const tableData = this.util.transformChartDataToTable(this.switchAgeData);
          this.tableColumns.set(tableData.columns);
          this.displayData.set(tableData.data);
        } else {
          // Ensure table is cleared if no data
          this.switchAgeData = null;
          this.tableColumns.set([]);
          this.displayData.set([]);
        }
        
        // Set switch age loading state to false
        this.isSwitchAgeLoading = false;
      },
      error: (error) => {
        console.error('Unexpected error in loadSwitchAgeData:', error);
        this.isSwitchAgeLoading = false;
        // Ensure table is cleared on error
        this.switchAgeData = null;
        this.tableColumns.set([]);
        this.displayData.set([]);
      }
    });
  }

  loadSwitchStatusSummary(): void {
    this.isStatusSummaryLoading = true;
    
    this.fabricDataService.fetchFabricChartWithDate('perse_switch_statusupdate_summaries', this.fromDate, this.toDate, this.selectedSupplier).pipe(
      catchError(error => {
        console.error('Error fetching switch status summary data:', error);
        this.util.openSnackBar('Error fetching switch status summary data', 'Close', 'error');
        return of({ result: { data: null } });
      })
    ).subscribe({
      next: (response: any) => {
        if (response.result?.data) {
          // Store raw TableData directly
          this.switchStatusSummaryTableData.set(response.result.data as TableData);
        } else {
          // Clear table if no data
          this.switchStatusSummaryTableData.set(null);
        }
        
        this.isStatusSummaryLoading = false;
      },
      error: (error) => {
        console.error('Unexpected error in loadSwitchStatusSummary:', error);
        this.isStatusSummaryLoading = false;
        // Clear table on error
        this.switchStatusSummaryTableData.set(null);
      }
    });
  }

  loadSwitchPendingIds(): void {
    this.isPendingIdsLoading = true;
    
    this.fabricDataService.fetchFabricChartWithDate('perse_switch_pendingIds', this.fromDate, this.toDate, this.selectedSupplier).pipe(
      catchError(error => {
        console.error('Error fetching switch pending IDs data:', error);
        this.util.openSnackBar('Error fetching switch pending IDs data', 'Close', 'error');
        return of({ result: { data: null } });
      })
    ).subscribe({
      next: (response: any) => {
        if (response.result?.data) {
          const tableData = response.result.data as TableData;
          // Transform TableData to the format expected by app-table
          const columns: TableColumn[] = tableData.headers.map(header => ({
            key: header,
            header: header,
            sortable: true,
            filterable: true,
            type: ColumnType.String
          }));
          
          const rows = tableData.rows.map(row => {
            const obj: any = {};
            tableData.headers.forEach((header, index) => {
              obj[header] = row[index];
            });
            return obj;
          });
          
          this.pendingIdsTableColumns.set(columns);
          this.pendingIdsDisplayData.set(rows);
        } else {
          // Clear table if no data
          this.pendingIdsTableColumns.set([]);
          this.pendingIdsDisplayData.set([]);
        }
        
        this.isPendingIdsLoading = false;
      },
      error: (error) => {
        console.error('Unexpected error in loadSwitchPendingIds:', error);
        this.isPendingIdsLoading = false;
        // Clear table on error
        this.pendingIdsTableColumns.set([]);
        this.pendingIdsDisplayData.set([]);
      }
    });
  }

  /**
   * Download pending switch IDs as CSV
   * Uses the same API data that populates the pending IDs table
   */
  downloadPendingSwitchIds(): void {
    const data = this.pendingIdsDisplayData();
    const columns = this.pendingIdsTableColumns();
    
    if (!data.length || !columns.length) {
      this.util.openSnackBar('No data available to download', 'Close', 'error');
      return;
    }
    
    const columnKeys = columns.map(col => col.key);
    const columnAlias = Object.fromEntries(columns.map(col => [col.key, col.header]));
    
    exportTableAsCSV('pending-switch-ids', data, columnKeys, columnAlias);
  }

  /**
   * Load suppliers from the fabric data service
   * This needs to be called before loading any other data
   */
  loadSuppliers(): void {
    this.isSuppliersLoading = true;
    this.noSuppliersFound = false;
    
    this.fabricDataService.listSuppliers().pipe(
      catchError(error => {
        console.error('Error fetching suppliers:', error);
        this.util.openSnackBar('Error fetching suppliers', 'Close', 'error');
        this.noSuppliersFound = true;
        return of([]);
      })
    ).subscribe({
      next: (suppliers) => {
        if (suppliers && suppliers.length > 0) {
          // Map suppliers to dropdown options
          this.supplierOptions = suppliers.map(supplier => ({ value: supplier, label: supplier }));
          
          // Set the first supplier as the default selected
          this.selectedSupplier = suppliers[0];
          
          // Now that we have suppliers, load the rest of the data
          this.reloadData(); // Use reloadData to load everything
        } else {
          this.noSuppliersFound = true;
          this.util.openSnackBar('No switch providers found', 'Close', 'error');
        }
        this.isSuppliersLoading = false;
      },
      error: (error) => {
        console.error('Unexpected error in loadSuppliers:', error);
        this.isSuppliersLoading = false;
        this.noSuppliersFound = true;
      }
    });
  }

  /**
   * Handle supplier selection change
   */
  onSupplierChange(): void {
    // Reload data with the newly selected supplier and current date range
    this.reloadData();
  }

  loadRegionWiseMap(){
    this.regionWiseMapChart = {};
    this.isMapLoading = true
    const endpoint = 'region_wise_map';
    this.fabricDataService.fetchFabricChartWithDate(endpoint, this.fromDate, this.toDate, this.selectedSupplier).subscribe({
      next: (resp:any) => {      
        if (!resp?.result?.data) {
          this.regionWiseMapChart.series = []; // Ensure map displays 'No data available'
          this.isMapLoading = false;
          return;
        }

        const regionWiseMapChart:Highcharts.Options = {
          chart: {
            map: mapCollection,
            backgroundColor: '#f7fafa'
          },
          title: {
            text: 'Switch Request Region Wise'
          },
          mapNavigation: {
            enabled: true
          },
          tooltip: {
            headerFormat: '',
            pointFormat: 'Switch postal code: {point.custPostalCode}<br>Switch status: {point.switchStatus}'
          },
          series: [
            {
              name: '',
              mapData: mapCollection,
              borderColor: '#666',
              type: 'map',
              showInLegend: false,
            },
          ]
        };
        const groupedData = this.groupBySwitchStatus(resp.result.data);
        const statusColors: Record<string, string> = {
          AWAITING_EXPORT: '#ffdad6', // Orange
          CLIENT_CALLBACK_ERROR: '#ba1a1a', // Red
          EXPORTED_SUPPLIER: '#006973', // Green
          SUPPLIER_ACCEPTED_COMPLETED: '#7fd3e0', // Blue
          SUPPLIER_API_REQUEST_ERROR: '#410002', // Purple
          SUPPLIER_OBJECTED: '#FFC107', // Amber
          SUPPLIER_REJECTED: '#DC143C', // Crimson
        };

        Object.keys(groupedData).forEach((key) => {
          if (regionWiseMapChart.series) {
            regionWiseMapChart.series.push({
              name: key,
              type: 'mappoint',
              data: groupedData[key] as any,
              marker: {
                symbol: 'circle',
                fillColor: statusColors[key] || '#000000', // Default to black if no match
              },
            });
          }
        });
        this.regionWiseMapChart = regionWiseMapChart;
        this.isMapLoading = false;
      },
      error: (error) => {
        this.isMapLoading = false
        this.regionWiseMapChart.series = []; // Ensure map displays 'No data available' on error
        console.error(`Error fetching ${endpoint} chart:`, error);
        this.util.openSnackBar(`Error fetching ${endpoint.replace('_', ' ')} chart`, 'Close', 'error');
      }
    });
  }

  groupBySwitchStatus(data: Array<any>) {
    return data.reduce((acc, item) => {
        const key = item.switchStatus;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push({
          switchStatus: item.switchStatus,
          lat: item.latitude,
          lon: item.longitude,
          custPostalCode: item.custPostalCode,
        });
        return acc;
    }, {});
  }

  pointInPolygon(point:any, polygon:any) {
    const x = point[0], y = point[1];
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i][0], yi = polygon[i][1];
        const xj = polygon[j][0], yj = polygon[j][1];

        const intersect = ((yi > y) !== (yj > y)) &&
            (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
  }

}
